"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { User, onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "@/lib/firebase";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  logout: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verificar se o Firebase Auth está disponível
    if (!auth) {
      setLoading(false);
      return;
    }

    // Timeout de segurança caso o Firebase não responda
    const timeoutId = setTimeout(() => {
      setLoading(false);
    }, 3000);

    try {
      const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        clearTimeout(timeoutId);
        setUser(firebaseUser);
        setLoading(false);
      });
      return () => {
        clearTimeout(timeoutId);
        unsubscribe();
      };
    } catch {
      clearTimeout(timeoutId);
      setLoading(false);
      return () => clearTimeout(timeoutId);
    }
  }, []);

  const logout = async () => {
    await signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
